import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setCredentials, clearCredentials } from "./authSlice";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://stagingapi.acadimyst.com/";

// Base query for all authenticated requests — sends httpOnly cookie automatically
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Dedicated base query for the refresh endpoint — no Authorization header
const baseQueryForRefresh = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: "include",
});

// Shared refresh promise — prevents multiple simultaneous refresh calls (mutex pattern)
let refreshingPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Check if the session has already expired beyond maxSessionUntil
    const maxSessionUntil = api.getState().auth.maxSessionUntil;
    if (maxSessionUntil && new Date() > new Date(maxSessionUntil)) {
      api.dispatch(clearCredentials());
      window.location.href = "/login";
      return result;
    }

    // Deduplicate concurrent refresh calls with a shared promise
    if (!refreshingPromise) {
      refreshingPromise = baseQueryForRefresh(
        {
          
          url: "api/v1/en/employee/auth/refresh-token",
          method: "POST",
          body: {},
        },
        api,
        extraOptions,
      ).finally(() => {
        refreshingPromise = null;
      });
    }

    const refreshResult = await refreshingPromise;

    if (refreshResult?.data) {
      const newToken = refreshResult.data?.data?.data?.token;
      const newMaxSessionUntil =
        refreshResult.data?.data?.data?.maxSessionUntil;
      if (newToken) {
        api.dispatch(
          setCredentials({
            token: newToken,
            maxSessionUntil: newMaxSessionUntil,
          }),
        );
        // Retry the original request with the new token
        result = await baseQueryWithAuth(args, api, extraOptions);
      }
    } else {
      // Refresh failed — clear auth and redirect to login
      api.dispatch(clearCredentials());
      window.location.href = "/login";
    }
  }

  return result;
};

export const apiSlice = createApi({
  reducerPath: "api",

  baseQuery: baseQueryWithReauth,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  tagTypes: [
    "Login",
    "SpeakingAccessorStudents",
    "WritingAccessorStudents",
    "WritingAccessorComments",
    "TeacherAvailabilityRules",
    "TeacherAvailableSlots",
    "TeacherBookedSlots",
  ],
  endpoints: (builder) => ({
    // your endpoints here
  }),
});
